export const roles = [
    {label: "Repartidor", value: "delivery"},
    {label: "Cliente", value: "client"},
    {label: "Administrador", value: "admin"},
]

export const roleRoutes = {
    admin: "66edc1f613059690d19d6ea5",
    client: "66edc2fc13059690d19d6ead", // KAM o AM dependiendo del negocio
    delivery: "66edc29a13059690d19d6ea9",
    supervisor: "66edc25a13059690d19d6ea7",
    support: "66edc2c613059690d19d6eab",
    superadmin: "66edc32f13059690d19d6eb1", // Superadministrador logístico
    systemSuperadmin: "6a217285bc54bd5a025cdf4b", // Superadministrador del sistema (plataforma completa)
    deliveryCommerceFleet: "694215139c2e62f246ebe1e0", // Repartidor de flota propia de comercio
    holdingAdmin: "6a034b72ddfb4834b224ac32", // Admin de holding (multicurrier con N comercios hijos)
    controlTower: "6a3e1c0d8f4b2a5c7e9d0142", // Display de sala "Centro de Control Logístico IA" (solo lectura, kiosko)
  };

/**
 * Roles de back-office: el mismo conjunto que `RequireAdmin`, que
 * `INCIDENT_STAFF_ROLE_IDS` de microservice-packages (IncidentStaffGuard) y que
 * `ADMIN_ROLE_IDS` del BFF. Si divergen, alguien ve una pantalla y después
 * recibe 403 al usarla. Se compara por _id porque `name` y `type` son editables.
 */
export const BACKOFFICE_ROLE_IDS = Object.freeze([
  roleRoutes.admin,
  roleRoutes.superadmin,
  roleRoutes.systemSuperadmin,
  roleRoutes.support,
  roleRoutes.supervisor,
]);

/**
 * Quién entra a la vista "Atención e incidencias": decisión de producto de la
 * Fase 1, no de seguridad. A diferencia de BACKOFFICE_ROLE_IDS no incluye al
 * superadmin del sistema; el backend igual lo admitiría.
 */
export const INCIDENT_DESK_ROLE_IDS = Object.freeze([
  roleRoutes.admin,
  roleRoutes.superadmin,
  roleRoutes.support,
  roleRoutes.supervisor,
]);

/**
 * El AuthContext normaliza `role` al _id, pero el JWT y `getUser` lo traen
 * poblado (`{ _id, type }`): se aceptan las tres formas.
 * @param {string|{role?: string|{_id?: string}}|null|undefined} roleIdOrUser
 * @returns {string|null}
 */
function resolveRoleId(roleIdOrUser) {
  if (typeof roleIdOrUser === "string") return roleIdOrUser;
  const role = roleIdOrUser?.role;
  if (typeof role === "string") return role;
  if (typeof role?._id === "string") return role._id;
  return null;
}

/**
 * true si el rol puede crear y gestionar incidencias según el backend. Es solo
 * UX (mostrar u ocultar acciones): el permiso real lo aplica IncidentStaffGuard
 * con 403.
 * @param {string|{role?: string|{_id?: string}}|null|undefined} roleIdOrUser - _id del rol o usuario
 * @returns {boolean}
 */
export function canManageIncidents(roleIdOrUser) {
  const roleId = resolveRoleId(roleIdOrUser);
  return roleId !== null && BACKOFFICE_ROLE_IDS.includes(roleId);
}

/**
 * true si el usuario puede escribir a repartidores desde la mesa de ayuda
 * (iniciar conversación, agregar números y editar el teléfono registrado):
 * los mismos cuatro roles de la vista de incidencias. Solo UX; el BFF y los
 * microservicios aplican el permiso real.
 * @param {string|{role?: string|{_id?: string}}|null|undefined} roleIdOrUser - _id del rol o usuario
 * @returns {boolean}
 */
export function canWriteToDrivers(roleIdOrUser) {
  const roleId = resolveRoleId(roleIdOrUser);
  return roleId !== null && INCIDENT_DESK_ROLE_IDS.includes(roleId);
}

/**
 * Traduce un roleId de MongoDB al string semantico que espera la API de AI.
 * @param {string} roleId
 * @returns {"user"|"agent"|"supervisor"}
 */
export function mapRoleToAI(roleId) {
  switch (roleId) {
    case roleRoutes.client:
      return "user";
    case roleRoutes.support:
      return "agent";
    case roleRoutes.supervisor:
    case roleRoutes.admin:
    case roleRoutes.superadmin:
    case roleRoutes.systemSuperadmin:
      return "supervisor";
    default:
      return "user";
  }
}